/**
 * A member's highest org role, as a coloured chip. Ported from `bimgo-ai-web`'s
 * `RoleBadge` — same role order and palette, adapted off tailwind to inline
 * colours so it needs no utility CSS. `system_admin` is never shown.
 */
import { useLocale } from "../../i18n/LocaleProvider";

/** Org-scoped roles the UI exposes, ordered highest → lowest. */
export const ORG_ROLES = [
  "organization_admin",
  "project_admin",
  "advance_user",
  "standard_user",
] as const;

const FALLBACK_COLOR = { bg: "#f9fafb", fg: "#374151", border: "#e5e7eb" };

const ROLE_COLORS: Record<string, { bg: string; fg: string; border: string }> = {
  organization_admin: { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0" },
  project_admin: { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe" },
  advance_user: { bg: "#fffbeb", fg: "#b45309", border: "#fde68a" },
  standard_user: FALLBACK_COLOR,
};

/** Highest role held (first match in ORG_ROLES order); system_admin hidden. */
export function primaryRole(roles: string[]): string | undefined {
  const visible = roles.filter((r) => r !== "system_admin");
  return ORG_ROLES.find((r) => visible.includes(r)) ?? visible[0];
}

function ShieldIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

export function RoleBadge({ roles }: { roles: string[] }) {
  const { t } = useLocale();
  const role = primaryRole(roles);
  if (!role) return null;
  const c = ROLE_COLORS[role] ?? FALLBACK_COLOR;
  const label = t(`settings.org.members.role.${role}`);
  return (
    <span className="role-badge" style={{ background: c.bg, color: c.fg, borderColor: c.border }}>
      <ShieldIcon />
      {label === `settings.org.members.role.${role}` ? role : label}
    </span>
  );
}
